import React, { createContext, useContext } from 'react';
import { useAuth } from '@clerk/clerk-react';

/**
 * El token de sesión, accesible desde cualquier sitio SIN saber si Clerk está montado.
 *
 * ⚠️ POR QUÉ HACE FALTA ESTO. `useAuth()` de Clerk **lanza** si no hay `ClerkProvider` encima,
 * y aquí el provider es condicional: sin `VITE_CLERK_PUBLISHABLE_KEY` la aplicación funciona
 * en modo invitado y no se monta. Un hook no se puede llamar condicionalmente, así que hasta
 * ahora cada pantalla que quería el token tenía que partirse en dos componentes —uno con Clerk
 * y otro sin él— y las que no se molestaban simplemente **no lo pasaban**.
 *
 * Eso no era teórico: `ActivityPage`, `CustomerActivityDrawer` y `RecentBookingsSection` pedían
 * la credencial de identidad sin sesión, y mientras booking-service estuvo abierto no se notó.
 * Al cerrarlo el 5-sep-2026, quien entraba con su cuenta desde otro dispositivo veía un error
 * en sus propias reservas.
 *
 * Con esto la condición vive en UN sitio y el resto pregunta sin enterarse.
 */
const CustomerAuthContext = createContext<{
  getToken: (opts?: { template?: string }) => Promise<string | null>;
}>({
  /** Sin Clerk montado no hay sesión que dar. No es un error: es el modo invitado. */
  getToken: async () => null,
});

/** Dentro de `ClerkProvider`: el `getToken` de verdad. */
function ConClerk({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  return (
    <CustomerAuthContext.Provider value={{ getToken }}>{children}</CustomerAuthContext.Provider>
  );
}

/**
 * ⚠️ SE MONTA SIEMPRE, con Clerk o sin él, y por eso quien consume no necesita saber cuál de
 * los dos casos es. Si solo se montara con Clerk, el defecto del contexto haría de red — y una
 * red que solo actúa cuando alguien se olvida de montar el provider es indistinguible de un
 * fallo silencioso.
 */
export function CustomerAuthProvider({
  hayClerk,
  children,
}: {
  hayClerk: boolean;
  children: React.ReactNode;
}) {
  if (!hayClerk) return <>{children}</>;
  return <ConClerk>{children}</ConClerk>;
}

export function useCustomerAuth() {
  return useContext(CustomerAuthContext);
}
